import {
  useState,
} from 'react';
import axios from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  EditRounded,
  GroupRounded,
  PersonAddRounded,
  RefreshRounded,
  WorkOutlineRounded,
} from '@mui/icons-material';

import {
  createStacManagedTask,
  createStacManagedWorker,
  listStacManagedProjects,
  listStacManagedTasks,
  listStacManagedWorkers,
  updateStacManagedTaskAssignments,
} from '../services/stacService';
import type {
  StacManagedTask,
  StacWorker,
} from '../types/stac';


function getApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const detail =
    error.response?.data?.detail;

  return (
    typeof detail === 'string'
    && detail.trim()
  )
    ? detail
    : fallback;
}


function workerLabel(
  worker: StacWorker,
): string {
  return worker.display_name;
}


export default function StacTeamWorkView() {
  const queryClient =
    useQueryClient();

  const {
    enqueueSnackbar,
  } = useSnackbar();

  const [
    newWorkerOpen,
    setNewWorkerOpen,
  ] = useState(false);

  const [
    newWorkerEmail,
    setNewWorkerEmail,
  ] = useState('');

  const [
    newWorkerDisplayName,
    setNewWorkerDisplayName,
  ] = useState('');

  const [
    taskTitle,
    setTaskTitle,
  ] = useState('');

  const [
    selectedWorkerIds,
    setSelectedWorkerIds,
  ] = useState<string[]>([]);

  const [
    selectedProjectId,
    setSelectedProjectId,
  ] = useState('');

  const [
    editingTask,
    setEditingTask,
  ] = useState<StacManagedTask | null>(
    null
  );

  const [
    editWorkerIds,
    setEditWorkerIds,
  ] = useState<string[]>([]);

  const workersQuery = useQuery({
    queryKey: [
      'stac-managed-workers',
    ],
    queryFn:
      listStacManagedWorkers,
    retry: false,
  });

  const tasksQuery = useQuery({
    queryKey: [
      'stac-managed-tasks',
    ],
    queryFn:
      listStacManagedTasks,
    retry: false,
  });

  const projectsQuery = useQuery({
    queryKey: [
      'stac-managed-projects',
    ],
    queryFn:
      listStacManagedProjects,
    retry: false,
  });

  const workers =
    workersQuery.data ?? [];

  const tasks =
    tasksQuery.data ?? [];

  const projects =
    projectsQuery.data ?? [];

  const refreshManagement =
    async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            'stac-managed-workers',
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'stac-managed-tasks',
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'stac-managed-projects',
          ],
        }),
      ]);
    };

  const createWorkerMutation =
    useMutation({
      mutationFn:
        createStacManagedWorker,

      onSuccess: async (worker) => {
        setNewWorkerOpen(false);
        setNewWorkerEmail('');
        setNewWorkerDisplayName('');

        enqueueSnackbar(
          `${worker.display_name} incorporado a STAC.`,
          {
            variant: 'success',
          },
        );

        await refreshManagement();
      },

      onError: (error) => {
        enqueueSnackbar(
          getApiErrorMessage(
            error,
            'No se pudo incorporar la persona a STAC.',
          ),
          {
            variant: 'error',
          },
        );
      },
    });

  const createTaskMutation =
    useMutation({
      mutationFn:
        createStacManagedTask,

      onSuccess: async () => {
        setTaskTitle('');
        setSelectedWorkerIds([]);
        setSelectedProjectId('');

        enqueueSnackbar(
          'Trabajo creado y asignado.',
          {
            variant: 'success',
          },
        );

        await Promise.all([
          refreshManagement(),
          queryClient.invalidateQueries({
            queryKey: [
              'stac-tasks',
            ],
          }),
        ]);
      },

      onError: (error) => {
        enqueueSnackbar(
          getApiErrorMessage(
            error,
            'No se pudo crear el trabajo.',
          ),
          {
            variant: 'error',
          },
        );
      },
    });

  const updateAssignmentsMutation =
    useMutation({
      mutationFn: ({
        taskId,
        workerIds,
      }: {
        taskId: string;
        workerIds: string[];
      }) =>
        updateStacManagedTaskAssignments(
          taskId,
          {
            worker_ids:
              workerIds,
          },
        ),

      onSuccess: async () => {
        setEditingTask(null);
        setEditWorkerIds([]);

        enqueueSnackbar(
          'Asignaciones actualizadas.',
          {
            variant: 'success',
          },
        );

        await Promise.all([
          refreshManagement(),
          queryClient.invalidateQueries({
            queryKey: [
              'stac-tasks',
            ],
          }),
        ]);
      },

      onError: (error) => {
        enqueueSnackbar(
          getApiErrorMessage(
            error,
            'No se pudieron actualizar las asignaciones.',
          ),
          {
            variant: 'error',
          },
        );
      },
    });

  const handleCreateWorker = () => {
    const email =
      newWorkerEmail.trim();

    if (!email) {
      return;
    }

    createWorkerMutation.mutate({
      email,
      display_name:
        newWorkerDisplayName.trim()
        || null,
    });
  };

  const handleCreateTask = () => {
    const title =
      taskTitle.trim();

    if (
      !title
      || !selectedWorkerIds.length
    ) {
      return;
    }

    createTaskMutation.mutate({
      title,
      worker_ids:
        selectedWorkerIds,
      project_id:
        selectedProjectId
        || null,
    });
  };

  const handleOpenAssignments = (
    task: StacManagedTask,
  ) => {
    setEditingTask(task);

    setEditWorkerIds(
      task.assignments.map(
        (assignment) =>
          assignment.worker_id
      )
    );
  };

  const selectedWorkers =
    workers.filter(
      (worker) =>
        selectedWorkerIds.includes(
          worker.id
        )
    );

  const editingWorkers =
    workers.filter(
      (worker) =>
        editWorkerIds.includes(
          worker.id
        )
    );

  if (
    workersQuery.isLoading
    || tasksQuery.isLoading
    || projectsQuery.isLoading
  ) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border:
            '1px solid rgba(148,163,184,0.14)',
          bgcolor:
            'rgba(15,23,42,0.72)',
          p: 2.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <CircularProgress
            size={20}
          />

          <Typography
            sx={{
              color: '#94a3b8',
            }}
          >
            Cargando equipo y trabajo...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (
    workersQuery.isError
    || tasksQuery.isError
    || projectsQuery.isError
  ) {
    return (
      <Alert
        severity="error"
        variant="outlined"
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={
              <RefreshRounded />
            }
            onClick={() => {
              void Promise.all([
                workersQuery.refetch(),
                tasksQuery.refetch(),
                projectsQuery.refetch(),
              ]);
            }}
          >
            Reintentar
          </Button>
        }
      >
        No se pudo cargar la gestión
        de equipo STAC.
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border:
              '1px solid rgba(148,163,184,0.16)',
            bgcolor:
              'rgba(15,23,42,0.72)',
            p: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              md: 'center',
            }}
            spacing={1.5}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <GroupRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontSize: '1.05rem',
                    fontWeight: 900,
                  }}
                >
                  Equipo y trabajo
                </Typography>
              </Stack>

              <Typography
                sx={{
                  mt: 0.4,
                  color: '#94a3b8',
                  fontSize: '0.86rem',
                }}
              >
                Gestiona personas y
                asignaciones dentro de este
                contexto STAC.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={
                <RefreshRounded />
              }
              disabled={
                workersQuery.isFetching
                || tasksQuery.isFetching
              }
              onClick={() => {
                void refreshManagement();
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
              }}
            >
              Actualizar
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg:
                'minmax(280px, 0.75fr) '
                + 'minmax(0, 1.25fr)',
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border:
                '1px solid rgba(148,163,184,0.14)',
              bgcolor:
                'rgba(15,23,42,0.72)',
              p: 2.5,
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Personas STAC
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.2,
                      color: '#64748b',
                      fontSize: '0.78rem',
                    }}
                  >
                    {workers.length}
                    {' '}
                    {workers.length === 1
                      ? 'persona activa'
                      : 'personas activas'}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    <PersonAddRounded />
                  }
                  onClick={() =>
                    setNewWorkerOpen(
                      true
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Añadir
                </Button>
              </Stack>

              <Divider />

              <Stack spacing={0.75}>
                {workers.map(
                  (worker) => (
                    <Paper
                      key={worker.id}
                      elevation={0}
                      sx={{
                        borderRadius: 2.5,
                        border:
                          '1px solid rgba(148,163,184,0.11)',
                        bgcolor:
                          'rgba(2,6,23,0.28)',
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Box
                          sx={{
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                '#f8fafc',
                              fontWeight:
                                850,
                            }}
                          >
                            {
                              worker.display_name
                            }
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.2,
                              color:
                                '#64748b',
                              fontSize:
                                '0.75rem',
                            }}
                          >
                            Trabajador STAC
                          </Typography>
                        </Box>

                        {worker.can_manage_work ? (
                          <Chip
                            size="small"
                            label="Gestor"
                            variant="outlined"
                            sx={{
                              color:
                                '#7dd3fc',
                              borderColor:
                                'rgba(56,189,248,0.35)',
                              fontWeight:
                                800,
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Paper>
                  ),
                )}
              </Stack>
            </Stack>
          </Paper>

          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border:
                  '1px solid rgba(148,163,184,0.14)',
                bgcolor:
                  'rgba(15,23,42,0.72)',
                p: 2.5,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <AddRounded
                    sx={{
                      color: '#38bdf8',
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Crear trabajo
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.78rem',
                      }}
                    >
                      Crea una tarea y
                      asígnala a una o varias
                      personas.
                    </Typography>
                  </Box>
                </Stack>

                <TextField
                  label="Trabajo"
                  value={taskTitle}
                  onChange={(event) =>
                    setTaskTitle(
                      event.target.value
                    )
                  }
                  inputProps={{
                    maxLength: 240,
                  }}
                  fullWidth
                />

                <Autocomplete
                  multiple
                  options={workers}
                  value={
                    selectedWorkers
                  }
                  getOptionLabel={
                    workerLabel
                  }
                  isOptionEqualToValue={(
                    option,
                    value,
                  ) =>
                    option.id
                    === value.id
                  }
                  onChange={(
                    _event,
                    value,
                  ) =>
                    setSelectedWorkerIds(
                      value.map(
                        (worker) =>
                          worker.id
                      )
                    )
                  }
                  renderInput={(
                    params
                  ) => (
                    <TextField
                      {...params}
                      label="Asignar a"
                      placeholder="Selecciona personas"
                    />
                  )}
                />

                <TextField
                  select
                  label="Proyecto"
                  value={
                    selectedProjectId
                  }
                  onChange={(event) =>
                    setSelectedProjectId(
                      event.target.value
                    )
                  }
                  helperText={
                    'Opcional. Puedes clasificar la tarea más adelante.'
                  }
                  fullWidth
                >
                  <MenuItem value="">
                    Sin proyecto
                  </MenuItem>

                  {projects.map(
                    (project) => (
                      <MenuItem
                        key={project.id}
                        value={project.id}
                      >
                        {project.name}
                      </MenuItem>
                    ),
                  )}
                </TextField>

                <Box>
                  <Button
                    variant="contained"
                    startIcon={
                      <AddRounded />
                    }
                    disabled={
                      createTaskMutation
                        .isPending
                      || !taskTitle.trim()
                      || !selectedWorkerIds
                        .length
                    }
                    onClick={
                      handleCreateTask
                    }
                    sx={{
                      textTransform:
                        'none',
                      fontWeight: 900,
                    }}
                  >
                    {createTaskMutation
                      .isPending
                      ? 'Creando...'
                      : 'Crear y asignar'}
                  </Button>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border:
                  '1px solid rgba(148,163,184,0.14)',
                bgcolor:
                  'rgba(15,23,42,0.72)',
                p: 2.5,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <WorkOutlineRounded
                    sx={{
                      color: '#38bdf8',
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Trabajo del equipo
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.78rem',
                      }}
                    >
                      {tasks.length}
                      {' '}
                      {tasks.length === 1
                        ? 'tarea activa'
                        : 'tareas activas'}
                    </Typography>
                  </Box>
                </Stack>

                {tasks.length ? (
                  <Stack
                    divider={
                      <Divider flexItem />
                    }
                  >
                    {tasks.map(
                      (task) => (
                        <Stack
                          key={task.id}
                          direction={{
                            xs: 'column',
                            md: 'row',
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: 'stretch',
                            md: 'center',
                          }}
                          spacing={1.25}
                          sx={{
                            py: 1.35,
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  '#f8fafc',
                                fontWeight:
                                  850,
                              }}
                            >
                              {task.title}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={0.65}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{
                                mt: 0.75,
                              }}
                            >
                              {task.project_name ? (
                                <Chip
                                  size="small"
                                  label={
                                    task.project_name
                                  }
                                  sx={{
                                    color:
                                      '#7dd3fc',
                                    border:
                                      '1px solid rgba(56,189,248,0.35)',
                                    bgcolor:
                                      'rgba(14,165,233,0.08)',
                                    fontWeight:
                                      800,
                                  }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label="Sin proyecto"
                                  variant="outlined"
                                  sx={{
                                    color:
                                      '#64748b',
                                  }}
                                />
                              )}

                              {task.assignments
                                .map(
                                  (
                                    assignment
                                  ) => (
                                    <Chip
                                      key={
                                        assignment
                                          .worker_id
                                      }
                                      size="small"
                                      label={
                                        assignment
                                          .display_name
                                      }
                                      variant="outlined"
                                    />
                                  ),
                                )}
                            </Stack>
                          </Box>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              <EditRounded />
                            }
                            onClick={() =>
                              handleOpenAssignments(
                                task
                              )
                            }
                            sx={{
                              textTransform:
                                'none',
                              fontWeight:
                                800,
                            }}
                          >
                            Asignaciones
                          </Button>
                        </Stack>
                      ),
                    )}
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.88rem',
                    }}
                  >
                    Todavía no hay trabajo
                    gestionado para el equipo.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Stack>

      <Dialog
        open={newWorkerOpen}
        onClose={() => {
          if (
            !createWorkerMutation
              .isPending
          ) {
            setNewWorkerOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            backgroundImage: 'none',
            color: '#f8fafc',
            border:
              '1px solid rgba(148,163,184,0.18)',
          },
        }}
      >
        <DialogTitle>
          Incorporar persona a STAC
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              pt: 1,
            }}
          >
            <Alert
              severity="info"
              variant="outlined"
            >
              La persona debe tener ya una
              cuenta PROA. Introduce su email
              exacto.
            </Alert>

            <TextField
              label="Email PROA"
              type="email"
              value={newWorkerEmail}
              onChange={(event) =>
                setNewWorkerEmail(
                  event.target.value
                )
              }
              autoComplete="off"
              fullWidth
            />

            <TextField
              label="Nombre visible"
              value={
                newWorkerDisplayName
              }
              onChange={(event) =>
                setNewWorkerDisplayName(
                  event.target.value
                )
              }
              helperText={
                'Opcional. Si lo dejas vacío, se utilizará el nombre de la cuenta PROA.'
              }
              inputProps={{
                maxLength: 160,
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
          }}
        >
          <Button
            onClick={() =>
              setNewWorkerOpen(
                false
              )
            }
            disabled={
              createWorkerMutation
                .isPending
            }
            sx={{
              textTransform: 'none',
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={
              <PersonAddRounded />
            }
            disabled={
              createWorkerMutation
                .isPending
              || !newWorkerEmail
                .trim()
            }
            onClick={
              handleCreateWorker
            }
            sx={{
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            {createWorkerMutation
              .isPending
              ? 'Incorporando...'
              : 'Incorporar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingTask)}
        onClose={() => {
          if (
            !updateAssignmentsMutation
              .isPending
          ) {
            setEditingTask(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            backgroundImage: 'none',
            color: '#f8fafc',
            border:
              '1px solid rgba(148,163,184,0.18)',
          },
        }}
      >
        <DialogTitle>
          Asignaciones
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              pt: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {editingTask?.title}
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                }}
              >
                La tarea debe conservar
                al menos una persona asignada.
              </Typography>
            </Box>

            <Autocomplete
              multiple
              options={workers}
              value={editingWorkers}
              getOptionLabel={
                workerLabel
              }
              isOptionEqualToValue={(
                option,
                value,
              ) =>
                option.id
                === value.id
              }
              onChange={(
                _event,
                value,
              ) =>
                setEditWorkerIds(
                  value.map(
                    (worker) =>
                      worker.id
                  )
                )
              }
              renderInput={(
                params
              ) => (
                <TextField
                  {...params}
                  label="Personas asignadas"
                />
              )}
            />

            <Alert
              severity="info"
              variant="outlined"
            >
              No se puede retirar una tarea
              a una persona mientras la esté
              ejecutando activamente.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
          }}
        >
          <Button
            onClick={() =>
              setEditingTask(null)
            }
            disabled={
              updateAssignmentsMutation
                .isPending
            }
            sx={{
              textTransform: 'none',
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              updateAssignmentsMutation
                .isPending
              || !editingTask
              || !editWorkerIds.length
            }
            onClick={() => {
              if (!editingTask) {
                return;
              }

              updateAssignmentsMutation
                .mutate({
                  taskId:
                    editingTask.id,
                  workerIds:
                    editWorkerIds,
                });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            {updateAssignmentsMutation
              .isPending
              ? 'Guardando...'
              : 'Guardar asignaciones'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}